import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Robustly scroll to top on every pathname change.
 * Tries multiple scroll targets and uses rAF/timeouts to ensure it runs after render.
 */
function findCustomScrollContainer() {
  // common selector if you added one (optional)
  const byAttr = document.querySelector("[data-scroll-container]");
  if (byAttr) return byAttr;

  // try to find first element that is scrollable (overflow auto/scroll and scrollHeight > clientHeight)
  const all = Array.from(document.querySelectorAll("body *"));
  for (const el of all) {
    const style = window.getComputedStyle(el);
    const overflowY = style.overflowY || style.overflow;
    if (/(auto|scroll|overlay)/.test(overflowY) && el.scrollHeight > el.clientHeight + 1) {
      return el;
    }
  }
  return null;
}

export default function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    // run after paint
    const attempt = () => {
      // 1) window/document scrolling element
      try {
        if (document.scrollingElement) {
          document.scrollingElement.scrollTo && document.scrollingElement.scrollTo(0, 0);
          document.scrollingElement.scrollTop = 0;
        }
        document.documentElement && (document.documentElement.scrollTop = 0);
        document.body && (document.body.scrollTop = 0);
      } catch (e) {
        // ignore
      }

      // 2) custom scroll container if present
      const container = findCustomScrollContainer();
      if (container) {
        try {
          if (container.scrollTo) container.scrollTo({ top: 0, left: 0, behavior: "auto" });
          else container.scrollTop = 0;
        } catch (e) {}
      }
    };

    // Use rAF to let the new route render first
    requestAnimationFrame(() => {
      attempt();
      // extra safety: do it again in next frame and tiny timeout
      requestAnimationFrame(attempt);
      setTimeout(attempt, 50);
    });
  }, [pathname]);

  return null;
}
